import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../api';
import SiDotChart from './SiDotChart';

const fmtQty = (n) => (n == null ? '—' : Math.round(n).toLocaleString());
const wma = (values) => {
  if (!values.length) return 0;
  let num = 0, den = 0;
  values.forEach((v, i) => { num += v * (i + 1); den += i + 1; });
  return den ? num / den : 0;
};

export default function SiItemDetail({ item, period, segment, subsidiary, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [excluded, setExcluded] = useState({});

  useEffect(() => {
    setD(null); setExcluded({});
    api.salesIntelItemDetail(item, period, segment, subsidiary)
      .then(setD).catch(e => setErr(e.response?.data?.detail || e.message));
  }, [item, period, segment, subsidiary]);

  const points = useMemo(() => (d?.points || []).map((p, i) => ({ ...p, key: (p.tranid || 'x') + '-' + i })), [d]);
  const outliers = points.filter(p => p.outlier);
  const live = useMemo(() => {
    if (!d) return null;
    const series = d.buckets.map((b, i) =>
      points.filter(p => p.bucket === i && !excluded[p.key]).reduce((a, p) => a + p.qty, 0));
    const avg = wma(series);
    return { series, avg, forecast: avg * d.buckets.length };
  }, [d, points, excluded]);

  const stats = d?.stats || {};
  const toggle = (key) => setExcluded(x => ({ ...x, [key]: !x[key] }));

  return (
    <div className="modal-overlay" data-testid="si-item-modal" onClick={onClose}>
      <div className="modal si-rep-modal" onClick={e => e.stopPropagation()}>
        <div className="si-rep-head">
          <div className="si-rep-title">
            <h3 data-testid="si-item-title">{d ? (d.name || item) : item}</h3>
            <span className="si-rep-window">{d ? `${d.period_label} · ${d.period_start} → ${d.period_end}` : 'loading…'}</span>
          </div>
          <button className="modal-close" data-testid="si-item-close" onClick={onClose}>×</button>
        </div>
        {err && <div className="si-error" data-testid="si-item-error">{err}</div>}
        {!d && !err && <div className="si-drill-body">Loading item history…</div>}
        {d && (
          <div className="si-drill-body" data-testid="si-item-body">
            <div className="si-item-stats" data-testid="si-item-stats">
              <div className="si-fc-kpi" data-testid="si-item-start-stock">
                <span className="si-fc-num">{fmtQty(stats.start_stock)}{stats.start_stock != null && stats.start_stock_estimated && <i className="si-est">est</i>}</span>
                <span className="si-fc-lbl" title={stats.start_stock_basis || ''}>Start of stock balance</span>
              </div>
              <div className="si-fc-kpi" data-testid="si-item-on-po">
                <span className="si-fc-num">{stats.on_po != null ? fmtQty(stats.on_po) : '—'}</span>
                <span className="si-fc-lbl" title={d.po_note || ''}>On purchase orders{stats.in_transit ? ` · ${fmtQty(stats.in_transit)} in transit` : ''}</span>
              </div>
              <div className="si-fc-kpi" data-testid="si-item-sold">
                <span className="si-fc-num">{fmtQty(stats.sold)}</span>
                <span className="si-fc-lbl">Sold in period</span>
              </div>
              <div className="si-fc-kpi" data-testid="si-item-current-stock">
                <span className="si-fc-num">{fmtQty(stats.current_stock)}</span>
                <span className="si-fc-lbl">Current stock balance</span>
              </div>
              <div className="si-fc-kpi" data-testid="si-item-open-order">
                <span className="si-fc-num">{fmtQty(stats.open_on_order)}</span>
                <span className="si-fc-lbl">Open on order</span>
              </div>
            </div>
            {!d.po_available && d.po_note && <div className="si-notice" data-testid="si-item-po-note">⚠ {d.po_note}</div>}

            <h4 className="si-item-h">Order quantities over time</h4>
            <SiDotChart points={points} buckets={d.buckets} excluded={excluded} median={d.median_order_qty} />

            <div className="si-item-stats" data-testid="si-item-forecast">
              <div className="si-fc-kpi">
                <span className="si-fc-num" data-testid="si-item-wavg">{fmtQty(live?.avg)}</span>
                <span className="si-fc-lbl">Weighted avg per {d.bucket === 'week' ? 'week' : 'month'}</span>
              </div>
              <div className="si-fc-kpi">
                <span className="si-fc-num" data-testid="si-item-forecast-qty">{fmtQty(live?.forecast)}</span>
                <span className="si-fc-lbl">Forecast demand, next {d.period_label.replace('Last ', '').replace('Year to date', d.buckets.length + ' months')}</span>
              </div>
              <div className="si-fc-kpi">
                <span className="si-fc-num">{d.order_count.toLocaleString()}</span>
                <span className="si-fc-lbl">Orders in period · {outliers.length} flagged outlier{outliers.length === 1 ? '' : 's'}</span>
              </div>
            </div>

            <h4 className="si-item-h">Outlier orders <span className="si-fc-note">left in the average by default — untick one to see the weighting without it</span></h4>
            {outliers.length === 0 ? (
              <p className="si-fc-note" data-testid="si-item-no-outliers">No material outliers: no single order is large enough to sway the weighted average.</p>
            ) : (
              <table className="si-table" data-testid="si-item-outliers">
                <thead><tr><th>In average</th><th>Date</th><th>Sales order</th><th>Client</th><th className="num">Qty</th><th className="num">Sways avg</th></tr></thead>
                <tbody>
                  {outliers.map(p => (
                    <tr key={p.key} className={excluded[p.key] ? 'si-row-off' : ''}>
                      <td>
                        <input type="checkbox" checked={!excluded[p.key]} onChange={() => toggle(p.key)}
                          data-testid={'si-item-outlier-toggle-' + p.tranid} aria-label={'Include ' + p.tranid + ' in the weighted average'} />
                      </td>
                      <td>{p.date}</td>
                      <td>{p.tranid}</td>
                      <td className="si-item-name" title={p.client}>{p.client || '—'}</td>
                      <td className="num strong">{fmtQty(p.qty)}</td>
                      <td className="num">{p.impact_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <h4 className="si-item-h">Every order in the period</h4>
            <div className="si-rep-recent">
              <table className="si-table" data-testid="si-item-orders">
                <thead><tr><th>Date</th><th>Sales order</th><th>Client</th><th className="num">Qty</th></tr></thead>
                <tbody>
                  {points.map(p => (
                    <tr key={p.key} className={excluded[p.key] ? 'si-row-off' : ''}>
                      <td>{p.date}</td>
                      <td>{p.tranid}</td>
                      <td className="si-item-name" title={p.client}>{p.client || '—'}</td>
                      <td className="num">{fmtQty(p.qty)}{p.outlier && <span className="si-gen-pill">outlier</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="si-fc-note" style={{ marginTop: 10 }}>
              {stats.start_stock_basis} Stock figures come from the nightly NetSuite sync (midnight Melbourne){d.stock_updated_at ? ' — last pull ' + d.stock_updated_at.slice(0, 16).replace('T', ' ') : ''}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
